import type { AdMobBridge } from '../types/admob';

/**
 * iOS 환경인지 확인
 */
export const isIOSWebView = (): boolean => {
  return !!window.webkit?.messageHandlers?.AdMobRewardJsBridge;
};

type AdMobAction =
  | 'loadRewardedAd'
  | 'showRewardedAd'
  | 'loadRewardedInterstitialAd'
  | 'showRewardedInterstitialAd'
  | 'loadInterstitialAd'
  | 'showInterstitialAd';

type AdMobMessage = { action: AdMobAction } & Record<string, unknown>;

/**
 * 플랫폼별 기본 보상형 광고 단위 ID
 * featureKind: 'attendance' | 'ads' | 'interstitial'
 */
const DEFAULT_REWARDED_UNIT_ID: Record<
  'ios' | 'android',
  Record<'attendance' | 'ads' | 'interstitial', string>
> = {
  ios: {
    attendance: 'ca-app-pub-2783777678386131/5861538637',
    ads: 'ca-app-pub-2783777678386131/4507900845',
    interstitial: 'ca-app-pub-2783777678386131/3470646186'
  },
  android: {
    attendance: 'ca-app-pub-2783777678386131/1990043558',
    ads: 'ca-app-pub-2783777678386131/7121406146',
    interstitial: 'ca-app-pub-2783777678386131/9863119550'
  }
};

export const resolveDefaultRewardedUnitId = (
  featureKind?: 'attendance' | 'ads' | 'interstitial'
): string | undefined => {
  if (!featureKind) return undefined;

  if (window.webkit?.messageHandlers?.AdMobRewardJsBridge) {
    return DEFAULT_REWARDED_UNIT_ID.ios[featureKind];
  }
  if (window.AdMobRewardJsBridge?.postMessage) return DEFAULT_REWARDED_UNIT_ID.android[featureKind];
  return undefined;
};

/**
 * 플랫폼별 브릿지로 메시지 전송 (iOS: Object, Android: JSON string)
 */
export const postToAdMob = (action: AdMobAction, data: Record<string, unknown> = {}): boolean => {
  const message: AdMobMessage = { action, ...data };
  // iOS (WebKit)
  if (window.webkit?.messageHandlers?.AdMobRewardJsBridge) {
    window.webkit.messageHandlers.AdMobRewardJsBridge.postMessage(message);
    return true;
  }
  // Android (WebView)
  if (window.AdMobRewardJsBridge?.postMessage) {
    window.AdMobRewardJsBridge.postMessage(JSON.stringify(message));
    return true;
  }
  return false;
};

/**
 * AdMob Bridge 객체
 */
export const AdMob: AdMobBridge = {
  /**
   * 보상형 광고 로드
   * @param userId - SSV 검증을 위한 사용자 ID (필수)
   * @param adUnitId - 로드할 광고 단위 ID (선택, 없으면 플랫폼/featureKind별 기본값 사용)
   * @param adType - 광고 타입 ('rewarded' | 'rewardedInterstitial' | 'interstitial', 기본값: 'rewardedInterstitial')
   * @param featureKind - 비즈니스상 구분(출석/광고보기 등) 기본 단위 선택용
   */
  loadRewardedAd: (
    userId: string,
    adUnitId?: string,
    adType: 'rewarded' | 'rewardedInterstitial' | 'interstitial' = 'rewardedInterstitial',
    featureKind?: 'attendance' | 'ads' | 'interstitial'
  ) => {
    if (!userId) {
      return;
    }
    const finalAdUnitId = adUnitId ?? resolveDefaultRewardedUnitId(featureKind);

    if (adType === 'interstitial') {
      postToAdMob('loadInterstitialAd', { userId, adUnitId: finalAdUnitId, featureKind });
      return;
    }

    if (adType === 'rewardedInterstitial') {
      if (isIOSWebView()) {
        postToAdMob('loadRewardedAd', {
          userId,
          adUnitId: finalAdUnitId,
          featureKind,
          adType
        });
      } else {
        postToAdMob('loadRewardedInterstitialAd', {
          userId,
          adUnitId: finalAdUnitId,
          featureKind,
          adType
        });
      }
      return;
    }

    postToAdMob('loadRewardedAd', { userId, adUnitId: finalAdUnitId, featureKind, adType });
  },

  /**
   * 보상형 전면 광고 표시
   * 광고가 로드된 상태에서만 호출해야 함
   * @param adUnitId - 표시할 광고 단위 ID (선택)
   * @param featureKind - 비즈니스상 구분(출석/광고보기 등) 기본 단위 선택용
   */
  showRewardedAd: (
    adUnitId?: string,
    featureKind?: 'attendance' | 'ads' | 'interstitial',
    adType: 'rewarded' | 'rewardedInterstitial' | 'interstitial' = 'rewardedInterstitial'
  ) => {
    const finalAdUnitId = adUnitId ?? resolveDefaultRewardedUnitId(featureKind);

    if (adType === 'interstitial' || featureKind === 'interstitial') {
      postToAdMob('showInterstitialAd', { adUnitId: finalAdUnitId });
      return;
    }

    if (adType === 'rewardedInterstitial') {
      if (isIOSWebView()) {
        postToAdMob('showRewardedAd', { adUnitId: finalAdUnitId });
      } else {
        postToAdMob('showRewardedInterstitialAd', { adUnitId: finalAdUnitId });
      }
      return;
    }
    postToAdMob('showRewardedAd', { adUnitId: finalAdUnitId });
  }
};
